import { Address, EMPTY_ADDRESS } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser, StandardUserFieldKey } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { AlertCircle, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AutocompleteUser } from '../../../../components/AutocompleteUser';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DatePickerReadonlyInput } from '../../../../components/DatePickerReadonlyInput';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer, FlexContainerVertical } from '../../../../layout/Flex';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { fromMoment, toMomentFromAny } from '../../../../services/time';
import {
  getSortedStringSetFromUsers,
  useUsersByOrgId
} from '../../../../services/user';
import {
  buildUpdatePath,
  getInitialValue,
  isAddressEmpty,
  prepareSaveValue,
  sanitizeInput,
  validateEmail as validateEmailUtil,
  validateNumber as validateNumberUtil
} from './utils';

const WarningBanner = styled('div')(({ theme }) => ({
  backgroundColor: '#FEFAEC',
  borderRadius: 4,
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1)
}));

const WarningIcon = styled(AlertCircle)({
  flexShrink: 0,
  marginTop: 2
});

const WarningText = styled(Typography)({
  fontSize: 14
});

const LearnMoreLink = styled('a')({
  color: '#856404',
  textDecoration: 'underline'
});

const ClearButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1)
}));

const AddressFieldsContainer = styled(FlexContainerVertical)(({ theme }) => ({
  gap: theme.spacing(2)
}));

const AddressRow = styled(FlexContainer)(({ theme }) => ({
  gap: theme.spacing(2)
}));

export type FieldType =
  | 'string'
  | 'date'
  | 'number'
  | 'relationship'
  | 'address'
  | 'email';

export interface EditFieldDialogProps {
  open: boolean;
  onClose: () => void;
  user: Doc<IUser>;
  fieldKey: string;
  fieldType: FieldType;
  fieldLabel: string;
  orgId: string;
  isCustomField: boolean;
  onSaved?: (fieldPath: string, value: unknown) => void;
}

const TextFieldEditor = ({
  value,
  onChange,
  fieldKey,
  orgId,
  label
}: {
  value: string;
  onChange: (v: string) => void;
  fieldKey: string;
  orgId: string;
  label: string;
}) => {
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      const standardFields: StandardUserFieldKey[] = [
        'department',
        'location',
        'team',
        'role'
      ];
      if (standardFields.includes(fieldKey as StandardUserFieldKey)) {
        const values = await getSortedStringSetFromUsers(
          orgId,
          (u) => (u.data as any)[fieldKey]
        );
        setOptions(values);
      }
    };
    loadOptions();
  }, [fieldKey, orgId]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value}
      inputValue={value}
      onInputChange={(_, newValue) => onChange(sanitizeInput(newValue))}
      onChange={(_, newValue) => {
        // Handle selection from dropdown
        if (typeof newValue === 'string') {
          onChange(sanitizeInput(newValue));
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="outlined" fullWidth />
      )}
    />
  );
};

const DateFieldEditor = ({
  value,
  onChange,
  label
}: {
  value: Timestamp | null;
  onChange: (v: Timestamp | null) => void;
  label: string;
}) => {
  const { t } = useUnsafeTranslation();
  const momentValue = value
    ? (() => {
        try {
          const m = toMomentFromAny(value);
          return m.isValid() ? m : null;
        } catch {
          return null;
        }
      })()
    : null;

  return (
    <FlexContainerVertical>
      <DatePickerReadonlyInput
        label={label}
        value={momentValue}
        onChange={(date) => {
          if (date && date.isValid()) {
            onChange(fromMoment(date));
          }
        }}
        format="MMMM DD, YYYY"
        inputVariant="outlined"
        fullWidth
      />
      {value && (
        <ClearButton
          size="small"
          startIcon={<X size={14} />}
          onClick={() => onChange(null)}
        >
          {t('Clear date')}
        </ClearButton>
      )}
    </FlexContainerVertical>
  );
};

const NumberFieldEditor = ({
  value,
  onChange,
  label,
  error
}: {
  value: number | string;
  onChange: (v: number | string) => void;
  label: string;
  error: string | null;
}) => {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      fullWidth
      type="number"
      error={!!error}
      helperText={error}
    />
  );
};

const UserRelationshipEditor = ({
  value,
  onChange,
  label,
  orgId,
  onClear
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  orgId: string;
  onClear: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [users] = useUsersByOrgId(orgId);
  const [selectedUser, setSelectedUser] = useState<Doc<IUser> | null>(null);

  // Load the current user if value is set
  useEffect(() => {
    if (value && users) {
      const user = users.find((u) => u.id === value);
      setSelectedUser(user || null);
    } else {
      setSelectedUser(null);
    }
  }, [value, users]);

  return (
    <FlexContainerVertical>
      <AutocompleteUser
        orgId={orgId}
        value={selectedUser}
        onChange={(user) => {
          setSelectedUser(user);
          onChange(user?.id || '');
        }}
        label={label}
      />
      {value && (
        <ClearButton size="small" startIcon={<X size={14} />} onClick={onClear}>
          {t('Clear user')}
        </ClearButton>
      )}
    </FlexContainerVertical>
  );
};

const AddressFieldEditor = ({
  value,
  onChange,
  onClear
}: {
  value: Address | null;
  onChange: (v: Address | null) => void;
  onClear: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [address, setAddress] = useState<Address>(value || EMPTY_ADDRESS());

  useEffect(() => {
    setAddress(value || EMPTY_ADDRESS());
  }, [value]);

  useEffect(() => {
    onChange(isAddressEmpty(address) ? null : address);
  }, [address]);

  const updateField = (field: keyof Address, val: string) => {
    setAddress((prev) => ({ ...prev, [field]: sanitizeInput(val) }));
  };

  return (
    <AddressFieldsContainer>
      <TextField
        label={t('Name')}
        value={address.name || ''}
        onChange={(e) => updateField('name', e.target.value)}
        variant="outlined"
        fullWidth
      />
      <TextField
        label={t('Street Address 1')}
        value={address.addressLine1 || ''}
        onChange={(e) => updateField('addressLine1', e.target.value)}
        variant="outlined"
        fullWidth
      />
      <TextField
        label={t('Street Address 2')}
        value={address.addressLine2 || ''}
        onChange={(e) => updateField('addressLine2', e.target.value)}
        variant="outlined"
        fullWidth
      />
      <AddressRow>
        <TextField
          label={t('City')}
          value={address.city || ''}
          onChange={(e) => updateField('city', e.target.value)}
          variant="outlined"
          fullWidth
        />
        <TextField
          label={t('State/Region')}
          value={address.region || ''}
          onChange={(e) => updateField('region', e.target.value)}
          variant="outlined"
          fullWidth
        />
      </AddressRow>
      <AddressRow>
        <TextField
          label={t('Postal Code')}
          value={address.postalCode || ''}
          onChange={(e) => updateField('postalCode', e.target.value)}
          variant="outlined"
          fullWidth
        />
        <TextField
          label={t('Country')}
          value={address.country || ''}
          onChange={(e) => updateField('country', e.target.value)}
          variant="outlined"
          fullWidth
        />
      </AddressRow>
      {value && (
        <ClearButton size="small" startIcon={<X size={14} />} onClick={onClear}>
          {t('Clear address')}
        </ClearButton>
      )}
    </AddressFieldsContainer>
  );
};

const EmailFieldEditor = ({
  value,
  onChange,
  label,
  error
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  error: string | null;
}) => {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(sanitizeInput(e.target.value))}
      variant="outlined"
      fullWidth
      type="email"
      error={!!error}
      helperText={error}
    />
  );
};

export const EditFieldDialog = ({
  open,
  onClose,
  user,
  fieldKey,
  fieldType,
  fieldLabel,
  orgId,
  isCustomField,
  onSaved
}: EditFieldDialogProps) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();

  // Get current value
  const currentValue = useMemo(() => {
    if (isCustomField) {
      const customFields = user.data.customFields || {};
      return customFields[fieldKey];
    }
    return (user.data as any)[fieldKey];
  }, [user.data, fieldKey, isCustomField]);

  const initialValue = useMemo(() => getInitialValue(currentValue, fieldType), [
    currentValue,
    fieldType
  ]);

  // Local state for editing
  const [value, setValue] = useState<any>(initialValue);
  const [numberError, setNumberError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Reset value when dialog opens
  useEffect(() => {
    setValue(initialValue);
    setNumberError(null);
    setEmailError(null);
  }, [initialValue, open]);

  const validateNumber = (v: any): boolean => {
    const result = validateNumberUtil(v);
    if (!result.valid) {
      setNumberError(t(result.error));
      return false;
    }
    setNumberError(null);
    return true;
  };

  const validateEmail = (v: string): boolean => {
    const result = validateEmailUtil(v);
    if (!result.valid) {
      setEmailError(t(result.error));
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleSave = async () => {
    // Validate before saving
    if (fieldType === 'number' && !validateNumber(value)) {
      return;
    }
    if (fieldType === 'email' && !validateEmail(value)) {
      return;
    }

    // Prepare the value for saving
    const updateValue = prepareSaveValue(value, fieldType);

    // Build the update path - use dot notation for nested fields
    const updatePath = buildUpdatePath(fieldKey, isCustomField);

    // Call the Cloud Function to update the user field
    await callFirebaseFunction('user_updateField', {
      orgId: user.data.orgId,
      userId: user.id,
      fieldPath: updatePath,
      value: updateValue
    });

    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'User Profile Data Edited',
      Explanation: 'Admin user edited user data on their profile page',
      'User ID': user.id,
      'Field Key': fieldKey
    });

    onSaved?.(updatePath, updateValue);
    onClose();
  };

  const renderEditor = () => {
    switch (fieldType) {
      case 'string':
        return (
          <TextFieldEditor
            value={value || ''}
            onChange={setValue}
            fieldKey={fieldKey}
            orgId={orgId}
            label={fieldLabel}
          />
        );
      case 'date':
        return (
          <DateFieldEditor
            value={value}
            onChange={setValue}
            label={fieldLabel}
          />
        );
      case 'number':
        return (
          <NumberFieldEditor
            value={value ?? ''}
            onChange={(v) => {
              setValue(v);
              validateNumber(v);
            }}
            label={fieldLabel}
            error={numberError}
          />
        );
      case 'relationship':
        return (
          <UserRelationshipEditor
            value={value || ''}
            onChange={setValue}
            label={fieldLabel}
            orgId={orgId}
            onClear={() => setValue('')}
          />
        );
      case 'address':
        return (
          <AddressFieldEditor
            value={value}
            onChange={setValue}
            onClear={() => setValue(null)}
          />
        );
      case 'email':
        return (
          <EmailFieldEditor
            value={value || ''}
            onChange={(v) => {
              setValue(v);
              validateEmail(v);
            }}
            label={fieldLabel}
            error={emailError}
          />
        );
      default:
        return (
          <TextField
            label={fieldLabel}
            value={value || ''}
            onChange={(e) => setValue(sanitizeInput(e.target.value))}
            variant="outlined"
            fullWidth
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {t('Edit')} {fieldLabel}
      </DialogTitleWithTypography>
      <DialogContent>
        {renderEditor()}

        <WarningBanner>
          <WarningIcon size={20} />
          <WarningText>
            {t(
              'Your changes to this field can be overwritten by imports from external data sources or sub-tasks set up with this field as a data destination.'
            )}{' '}
            <LearnMoreLink
              href="https://help.dadohr.com/article/164-how-to-update-user-data-in-dado"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Learn more')}
            </LearnMoreLink>
          </WarningText>
        </WarningBanner>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          disabled={!!numberError || !!emailError}
          onClick={handleSave}
          pending={`${t('Saving')}...`}
        >
          {t('Save')}
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

// Re-export getFieldType from utils for consumers that import from this file
export { getFieldType } from './utils';
